/**
 * A value support pastes into tickets — email, UID, subscription or customer
 * id, order reference. Click the value, it's on the clipboard.
 */
import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

export function Copyable({
  value,
  children,
  className,
  mono,
}: {
  value: string;
  children?: React.ReactNode;
  className?: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Clipboard is blocked (insecure context, denied permission) — say
      // nothing rather than claim a copy that did not happen.
      return;
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? "Copied" : `Copy ${value}`}
      className={cn(
        "group inline-flex max-w-full items-center gap-1 text-left hover:text-primary",
        mono && "font-mono text-xs",
        className
      )}
    >
      <span className="truncate">{children ?? value}</span>
      {copied ? (
        <Check className="h-3 w-3 shrink-0 text-success" />
      ) : (
        <Copy className="h-3 w-3 shrink-0 opacity-0 transition-opacity group-hover:opacity-60" />
      )}
    </button>
  );
}
